import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { useAuth } from '../contexts/AuthContext';
import { user as userApi } from '../lib/api';
import {
  detectInitialLocale,
  getIntlLocale,
  LOCALE_STORAGE_KEY,
  normalizeLocale,
  setActiveLocale,
  translateText,
} from './runtime';

const LocaleContext = createContext(null);
const TRANSLATABLE_ATTRIBUTES = ['aria-label', 'placeholder', 'title'];
const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'CODE', 'PRE', 'TEXTAREA']);

function translateElement(root, locale) {
  if (!root || typeof document === 'undefined') return;

  const translateNode = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const parent = node.parentElement;
      if (!parent || SKIP_TAGS.has(parent.tagName) || parent.closest('[data-no-translate="true"]')) return;
      const next = translateText(node.nodeValue, locale);
      if (next !== node.nodeValue) node.nodeValue = next;
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    if (SKIP_TAGS.has(node.tagName) || node.closest('[data-no-translate="true"]')) return;
    TRANSLATABLE_ATTRIBUTES.forEach((attribute) => {
      const current = node.getAttribute(attribute);
      if (!current) return;
      const next = translateText(current, locale);
      if (next !== current) node.setAttribute(attribute, next);
    });
  };

  translateNode(root);
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT);
  while (walker.nextNode()) translateNode(walker.currentNode);
}

export function LocaleProvider({ children }) {
  const { user, checkAuth } = useAuth();
  const [locale, setLocaleState] = useState(() => detectInitialLocale());

  const setLocale = useCallback(async (value, { persist = true } = {}) => {
    const next = normalizeLocale(value);
    setLocaleState(next);
    setActiveLocale(next);
    window.localStorage.setItem(LOCALE_STORAGE_KEY, next);
    if (persist && user && user.locale !== next) {
      await userApi.update({ locale: next });
      await checkAuth();
    }
  }, [checkAuth, user]);

  useEffect(() => {
    const accountLocale = user && user.locale;
    if (accountLocale && accountLocale !== locale) {
      setLocale(accountLocale, { persist: false });
      return;
    }
    if (user && !accountLocale) {
      setLocale(locale, { persist: true }).catch(() => {});
    }
  }, [locale, setLocale, user]);

  useLayoutEffect(() => {
    setActiveLocale(locale);
    document.documentElement.lang = locale;
    translateElement(document.body, locale);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'characterData') {
          translateElement(mutation.target, locale);
        } else if (mutation.type === 'attributes') {
          translateElement(mutation.target, locale);
        } else {
          mutation.addedNodes.forEach((node) => translateElement(node, locale));
        }
      });
    });
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: TRANSLATABLE_ATTRIBUTES,
    });
    return () => observer.disconnect();
  }, [locale]);

  useEffect(() => {
    const originalConfirm = window.confirm;
    const originalAlert = window.alert;
    window.confirm = (message) => originalConfirm(translateText(String(message), locale));
    window.alert = (message) => originalAlert(translateText(String(message), locale));
    return () => {
      window.confirm = originalConfirm;
      window.alert = originalAlert;
    };
  }, [locale]);

  const value = useMemo(() => ({
    locale,
    intlLocale: getIntlLocale(locale),
    setLocale,
    t: (text) => translateText(text, locale),
    formatDate: (value, options) => new Intl.DateTimeFormat(getIntlLocale(locale), options).format(new Date(value)),
    formatNumber: (value, options) => new Intl.NumberFormat(getIntlLocale(locale), options).format(value),
  }), [locale, setLocale]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale() {
  const context = useContext(LocaleContext);
  if (!context) throw new Error('useLocale must be used inside LocaleProvider');
  return context;
}
